import * as amplitude from '@amplitude/analytics-browser';
import { getOrSetViewerId, resetViewerId } from '@modules/analytics/helpers';
import { logger } from '@modules/logger';
import { getCookie } from 'cookies-next';

import type { AnalyticsGroupEvent, AnalyticsIdentifyEvent } from './server-types';
import type { Analytics, AnalyticsUser, PageEvent, TrackEvent } from './types';

function getGaContext(): { sessionId: string | null } {
  return { sessionId: getCookie('_ga4') ?? '' };
}

const platform = 'dash';
const version = 1;

class AmplitudeAnalytics implements Analytics {
  name = 'amplitude';
  private initialized = false;
  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: historic
  private identifyInstance: InstanceType<typeof amplitude.Identify> | null = null;

  constructor() {
    this.initAmplitude();
  }

  private initAmplitude(): void {
    if (typeof window === 'undefined') return;

    if (this.initialized) return;

    const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_ANALYTICS_KEY;
    const serverUrl = process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_URL;

    // Fetch existing device_id cookie if available
    const deviceId = getOrSetViewerId();

    if (!apiKey) {
      logger.error('Amplitude API key is missing');
      return;
    }

    amplitude.init(apiKey, {
      deviceId,
      // Attribution writes utm_* / referrer / click IDs as user properties, which
      // marketing attribution reads (BIZ-2508). Every other tracker is pinned off:
      // in the SDK's object form an unspecified tracker defaults to ON, the dash
      // tracks its own page views, and the rest would add event volume.
      autocapture: {
        attribution: true,
        fileDownloads: false,
        formInteractions: false,
        pageViews: false,
        sessions: false,
        pageUrlEnrichment: false,
      },
      serverUrl: serverUrl ?? undefined,
    });

    this.initialized = true;
  }

  async initialize(): Promise<void> {
    return; // Optional async init hook
  }

  track(e: Partial<TrackEvent>): void {
    if (!e.name) {
      logger.warn('Amplitude track() called without name');
      return;
    }

    const eventProperties = {
      ...e.properties,
      platform,
      version,
      ga: getGaContext(),
      ...e.context,
    };

    try {
      amplitude.track(e.name, eventProperties);
    } catch (error) {
      logger.warn('Error calling Amplitude track()', { error });
    }
  }

  page(e: Partial<PageEvent>): void {
    // Segment adds the Viewed prefix to page_view events
    // Explicitly adding 'Viewed ' it here for consistency
    const eventName = 'Viewed page_view';
    const properties = {
      ...e.properties,
      host: e.properties?.host ?? window.location.hostname,
      search: window.location.search,
      platform,
      version,
      name: e.name ?? 'page_view',
      ga: getGaContext(),
      referrer: document.referrer,
      url: window.location.href,
      title: document.title,
    };

    try {
      amplitude.track(eventName, properties);
    } catch (error) {
      logger.warn('Error calling Amplitude page()', { error });
    }
  }

  identify(e: Partial<AnalyticsIdentifyEvent>): void {
    try {
      const identify = new amplitude.Identify();
      if (e.traits) {
        for (const [key, value] of Object.entries(e.traits)) {
          if (value !== null && !Array.isArray(value)) {
            identify.set(key, value);
          }
        }
      }
      if (e.user_id && !e.user_id?.includes('anon_')) {
        // never set anon or viewer IDs as user ID
        amplitude.setUserId(e.user_id);
      } else {
        // device ID is already set during init,
        // clear user ID to prevent user ID override
        amplitude.setUserId(undefined);
      }
      amplitude.identify(identify);
    } catch (error) {
      logger.error('Error calling Amplitude identify()', { error });
    }
  }

  // biome-ignore lint/suspicious/noEmptyBlockStatements: historic
  group(e: Partial<AnalyticsGroupEvent>): void {}

  user(): AnalyticsUser | undefined {
    try {
      return {
        id: () => amplitude.getUserId(),
        anonymousId: () => amplitude.getDeviceId(),
      };
    } catch (error) {
      logger.warn('Amplitude user() error', { error });
      return undefined;
    }
  }

  reset(): void {
    try {
      const newDeviceId = resetViewerId();
      amplitude.setUserId(undefined);
      amplitude.setDeviceId(newDeviceId);
    } catch (error) {
      logger.warn('Error resetting Amplitude', { error });
    }
  }
}

export const amplitudeAnalytics = new AmplitudeAnalytics();
